import type { LexBotDetail, LexIntentDetail } from "../types";

interface Props {
  detail: LexBotDetail | null;
  loading: boolean;
  error: string | null;
}

/**
 * The Lex bot as it stands today, shown before anything is created.
 *
 * The Convert button makes real resources in a real workspace, and until now
 * the only thing on screen to decide from was the bot's name. What matters is
 * the shape of what is about to be rebuilt: how many intents there are, and
 * what each one asks the caller for.
 *
 * Ordered as Lex orders it - slots by elicitation priority - rather than as
 * the migration will reorder them. This is the source, not the plan.
 */
function Intent({ intent }: { intent: LexIntentDetail }) {
  return (
    <li className="intent">
      <div className="intent-head">
        <span className="intent-name">{intent.name}</span>
        {intent.isFallback && <span className="tag">fallback</span>}
        <span className="intent-count">
          {intent.slots.length === 0
            ? "no slots"
            : `${intent.slots.length} slot${intent.slots.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {intent.description && (
        <p className="intent-desc">{intent.description}</p>
      )}

      {intent.slots.length > 0 && (
        <ul className="slots">
          {intent.slots.map((slot) => (
            <li key={slot.name}>
              <code className="slot-name">{slot.name}</code>
              <span className="slot-type">{slot.type}</span>
              {!slot.required && <span className="slot-optional">optional</span>}
              {slot.values.length > 0 && (
                <span className="slot-values">
                  {slot.values.slice(0, 4).join(", ")}
                  {slot.values.length > 4 && ` +${slot.values.length - 4}`}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/*
 * The panel before it has anything to show.
 *
 * Built from the real markup - the same head, the same intent rows, the same
 * slot list - with the text swapped for grey. A short placeholder followed by
 * a tall panel makes the page jump exactly where someone is about to click
 * Convert, which is the worst place on this screen for the layout to move.
 *
 * Three intents with 4, 2 and 3 slots: enough to fill the panel to roughly
 * the height a real bot fills it to.
 */
/** Per slot: the width of the name pill, then of the type beside it. */
const SKELETON_INTENTS = [
  {
    name: "58%",
    desc: "84%",
    slots: [[96, 118], [112, 140], [74, 96], [88, 128]],
  },
  { name: "44%", desc: null, slots: [[104, 132], [80, 92]] },
  { name: "62%", desc: "71%", slots: [[92, 110], [120, 148], [78, 104]] },
];

function DetailSkeleton() {
  return (
    <div className="detail" aria-hidden="true">
      <div className="detail-head">
        <span className="sk sk-line" style={{ width: 150, height: 14 }} />
        <span className="sk sk-line" style={{ width: 128, height: 12 }} />
      </div>

      <ul className="intents">
        {SKELETON_INTENTS.map((intent, i) => (
          <li key={i} className="intent">
            <div className="intent-head">
              <span
                className="sk sk-line"
                style={{ width: intent.name, height: 15, maxWidth: 220 }}
              />
              <span
                className="sk sk-line"
                style={{ width: 46, height: 12, marginLeft: "auto" }}
              />
            </div>

            {intent.desc && (
              <p className="intent-desc">
                <span
                  className="sk sk-line"
                  style={{ width: intent.desc, height: 11 }}
                />
              </p>
            )}

            <ul className="slots">
              {intent.slots.map(([pill, type], j) => (
                <li key={j}>
                  <span
                    className="sk sk-line"
                    style={{ width: pill, height: 19, borderRadius: 6 }}
                  />
                  <span
                    className="sk sk-line"
                    style={{ width: type, height: 11 }}
                  />
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BotDetail({ detail, loading, error }: Props) {
  if (loading) {
    return (
      <>
        <p className="sr-only" role="status">
          Reading the Lex bot
        </p>
        <DetailSkeleton />
      </>
    );
  }

  // Not fatal, and deliberately not a blocker: this panel is here to inform
  // the decision, not to gate it. A bot that cannot be summarised can still
  // be migrated, and the migration reports its own failure properly.
  if (error) {
    return (
      <p className="muted detail-note">
        Could not read the bot's design ({error}). You can still convert it.
      </p>
    );
  }

  if (!detail) return null;

  const routable = detail.intents.filter((i) => !i.isFallback);
  const slots = detail.intents.reduce((n, i) => n + i.slots.length, 0);

  return (
    <div className="detail">
      <div className="detail-head">
        <span className="detail-title">What this bot contains</span>
        <span className="detail-summary">
          {routable.length} intent{routable.length === 1 ? "" : "s"} &middot;{" "}
          {slots} slot{slots === 1 ? "" : "s"} &middot; {detail.localeId}
        </span>
      </div>

      <ul className="intents">
        {detail.intents.map((intent) => (
          <Intent key={intent.name} intent={intent} />
        ))}
      </ul>
    </div>
  );
}
